import React, { useState } from 'react'
import { companyLogo, authBg, hsclLogo, hsclLogo1, hsclLogo2, hero, agoraLogo, agoraLogo1, agoraCoreNew } from '../../assets/images'
import {
    IconEye,
    IconEyeOff,
    IconUsersGroup,
    IconClock,
    IconChartBar,
    IconCreditCard
} from '@tabler/icons-react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    React.useEffect(() => {
        setIsLoaded(true);
    }, []);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/dashboard';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Simple validation
        if (!email || !password) {
            setLoading(false);
            return;
        }

        // Attempt login
        const result = await login(email, password);

        if (result.success) {
            navigate(from, { replace: true });
        }

        setLoading(false);
    };

    return (
        <div className='flex h-svh overflow-hidden bg-white'>
            {/* Left Side: Brand & Features (40%) */}
            <div className='hidden lg:flex lg:w-[40%] flex-col bg-gradient-to-br from-[#011a4d] via-[#012E80] to-[#01245f] p-16 xl:p-24 text-white relative overflow-hidden'>
                {/* Mesh Glow Blobs */}
                <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-white/10 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#012E80]/30 rounded-full blur-[80px]"></div>

                {/* Logo top-left */}
                <div className={`mb-20 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                    <img src={agoraCoreNew} alt='AGORA' className='h-24 w-auto object-contain opacity-90 brightness-110' />
                </div>

                <div className='flex-1 flex flex-col justify-center relative z-10'>
                    <h2 className={`text-4xl xl:text-5xl font-bold mb-16 tracking-tight transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Hi, Welcome back
                    </h2>

                    <div className='space-y-10'>
                        {[
                            { icon: IconUsersGroup, title: 'Comprehensive HR Management', desc: 'Streamline employee data, departments, and attendance tracking in one central hub.' },
                            { icon: IconClock, title: 'Leave & Time Tracking', desc: 'Effortlessly manage leave requests, balances, and detailed timesheets for your workforce.' },
                            { icon: IconChartBar, title: 'Performance & KPI Insights', desc: 'Monitor organizational growth with intuitive KPI dashboards and performance history tracking.' },
                            { icon: IconCreditCard, title: 'Financial & Accounts Control', desc: 'Stay on top of your bottom line with integrated management of payments, expenses, and invoices.' }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`flex items-start gap-6 group transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${100 + (idx * 150)}ms` }}
                            >
                                <div className='p-3.5 bg-white/10 rounded-2xl group-hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/5'>
                                    <item.icon size={26} className='text-white' />
                                </div>
                                <div className='pt-1'>
                                    <h3 className='text-lg xl:text-xl font-bold mb-1.5 group-hover:text-white transition-colors'>{item.title}</h3>
                                    <p className='text-white/70 text-sm leading-relaxed max-w-sm font-light'>
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Side: Login Form (60%) */}
            <div className='w-full lg:w-[60%] flex flex-col bg-white overflow-y-auto px-8 md:px-16 lg:px-24 py-12'>
                <div className={`flex-1 flex items-center justify-center py-20 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className='w-full max-w-[460px]'>
                        <div className='text-center mb-14'>
                            <h1 className='text-4xl font-black text-gray-900 mb-3 tracking-tight'>Sign in</h1>
                            <p className="text-gray-500 font-light">Access the AgoraCore ERP Platform</p>
                        </div>

                        <form onSubmit={handleSubmit} className='flex flex-col gap-8'>
                            <div className='space-y-3'>
                                <label className='text-sm font-semibold text-gray-700 ml-1'>Email</label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    placeholder='Email'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#012E81] focus:ring-2 focus:ring-[#012E81]/20 transition-all outline-none text-gray-800'
                                />
                            </div>

                            <div className='space-y-3'>
                                <label className='text-sm font-semibold text-gray-700 ml-1'>Password</label>
                                <div className='relative'>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id='password'
                                        name='password'
                                        placeholder='Password'
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#012E81] focus:ring-2 focus:ring-[#012E81]/20 transition-all outline-none text-gray-800 pr-14'
                                    />
                                    <button
                                        type='button'
                                        onClick={() => setShowPassword(!showPassword)}
                                        className='absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors'
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <IconEyeOff size={22} /> : <IconEye size={22} />}
                                    </button>
                                </div>
                            </div>

                            <div className='flex items-center justify-between mt-2'>
                                <label className='flex items-center gap-3 cursor-pointer group'>
                                    <input
                                        type='checkbox'
                                        disabled={loading}
                                        className='w-5 h-5 rounded border-gray-300 text-[#012E80] focus:ring-[#012E80] transition-colors'
                                    />
                                    <span className='text-sm text-gray-600 group-hover:text-gray-900 transition-colors'>Remember me</span>
                                </label>
                                <Link to="/auth-forgot-password" virtual-link className='text-sm font-medium text-[#012E80] hover:text-[#011a4d] transition-colors'>
                                    Forgot your password?
                                </Link>
                            </div>

                            <button
                                type='submit'
                                className='mt-6 py-4 bg-[#012E80] hover:bg-[#011a4d] text-white text-lg font-bold rounded-xl transition-all duration-300 transform active:scale-[0.98] shadow-lg shadow-[#012E80]/20 flex justify-center items-center'
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-3">
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        <span>Signing in...</span>
                                    </div>
                                ) : 'Sign in'}
                            </button>
                        </form>
                    </div>
                </div>

                <div className={`mt-auto pt-10 flex justify-center transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className='flex items-center gap-3 opacity-70 hover:opacity-100 transition-all duration-500 py-4 px-8 rounded-full border border-gray-200 hover:border-[#012E80]/20 hover:bg-[#012E80]/5'>
                        <span className='text-[10px] uppercase tracking-[0.3em] font-medium text-gray-500'>Powered by</span>
                        <img src={hsclLogo2} alt='HSCL' className='h-6 w-auto object-contain brightness-95 group-hover:brightness-100' />
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; transform: scale(1); }
                    50% { opacity: 0.2; transform: scale(1.1); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite ease-in-out;
                }
            `}</style>
        </div>
    )
}
